import {
  createEntityAdapter,
  createSlice,
  current,
  PayloadAction
} from '@reduxjs/toolkit'
import { MetricTypeKeys } from '../metrics/types'
import { batchTransformEventData, transformEventData } from './functions'
import {
  EventEditDelta,
  EventEditedFields,
  EventEditTopField,
  EventsState,
  LineChartCoordinates,
  PendingEventEdit,
  RawDrillEventData,
  RawEventData
} from './types'

export const eventsAdapter = createEntityAdapter<RawEventData>()

const initialState: EventsState = {
  selectedEventId: '',
  columns: ['dist', 'speed'],
  flightGraphs: {},
  penaltyCountdownActive: false,
  eventValidationTableTimeDisplay: 'sessionTime',
  pendingDrill: null,
  pendingEdits: {}
}

export const initialEventsState = eventsAdapter.getInitialState(initialState)

type EventsSliceState = typeof initialEventsState

const TOP_FIELDS: EventEditTopField[] = ['ignore', 'outcomes', 'tags']

const union = <T extends string>(existing: T[], next: T[]) => [
  ...existing,
  ...next.filter((key) => !existing.includes(key))
]

const editedFieldsOf = (delta: EventEditDelta) => ({
  top: TOP_FIELDS.filter((key) => delta[key] !== undefined),
  properties: delta.properties ? Object.keys(delta.properties) : [],
  data: delta.data ? Object.keys(delta.data) : [],
  childPlayers: delta.childPlayers ? Object.keys(delta.childPlayers) : []
})

const mergeEditedFields = (
  existing: EventEditedFields,
  next: EventEditedFields
) => ({
  top: union(existing.top, next.top),
  properties: union(existing.properties, next.properties),
  data: union(existing.data, next.data),
  childPlayers: union(existing.childPlayers, next.childPlayers)
})

// Scalars and outcomes take the newest value; keyed maps merge per key
const mergeDelta = (queued: EventEditDelta | null, next: EventEditDelta) => {
  if (!queued) return next

  return {
    ...queued,
    ...next,
    ...(queued.properties || next.properties
      ? { properties: { ...queued.properties, ...next.properties } }
      : {}),
    ...(queued.data || next.data
      ? { data: { ...queued.data, ...next.data } }
      : {}),
    ...(queued.childPlayers || next.childPlayers
      ? { childPlayers: { ...queued.childPlayers, ...next.childPlayers } }
      : {}),
    ...(queued.replace || next.replace ? { replace: true } : {})
  }
}

const pickKeys = (
  value: object | null | undefined,
  keep: (key: string) => boolean
) =>
  Object.entries(value || {}).reduce<Record<string, unknown>>(
    (picked, [key, field]) => {
      if (keep(key)) picked[key] = field

      return picked
    },
    {}
  )

// Keys in `edited` come from `confirmed`, every other key from `held`
const mergeKeys = (
  held: object | null | undefined,
  confirmed: object | null | undefined,
  edited: string[]
) => ({
  ...pickKeys(held, (key) => !edited.includes(key)),
  ...pickKeys(confirmed, (key) => edited.includes(key))
})

const withConfirmedPlayer = (
  child: RawEventData,
  confirmedChildren: RawEventData[] | undefined,
  editedChildIds: string[]
) => {
  if (!editedChildIds.includes(child.id)) return child
  if (child.dataType !== 'PhysicalEvent') return child

  const source = confirmedChildren?.find(({ id }) => id === child.id)
  if (!source || source.dataType !== 'PhysicalEvent') return child

  return {
    ...child,
    data: {
      ...child.data,
      teamPersonSessionId: source.data.teamPersonSessionId
    }
  }
}

// Held server copy with every operator-edited field taken from `confirmed`
const mergeHeldCopy = (
  held: RawEventData,
  confirmed: RawEventData,
  edited: EventEditedFields
) => {
  const children = held.children?.length ? held.children : confirmed.children

  return Object.assign({}, confirmed, held, {
    ...(edited.top.includes('ignore') ? { ignore: confirmed.ignore } : {}),
    ...(edited.top.includes('outcomes')
      ? { outcomes: confirmed.outcomes }
      : {}),
    ...(edited.top.includes('tags') ? { tags: confirmed.tags } : {}),
    ...(edited.properties.length
      ? {
          properties: mergeKeys(
            held.properties,
            confirmed.properties,
            edited.properties
          )
        }
      : {}),
    ...(edited.data.length
      ? { data: mergeKeys(held.data, confirmed.data, edited.data) }
      : {}),
    ...(children
      ? {
          children: children.map((child) =>
            withConfirmedPlayer(child, confirmed.children, edited.childPlayers)
          )
        }
      : {})
  })
}

// A full save built on the held server copy, operator-edited fields from `latest`
export const replaceBodyOf = (entry: PendingEventEdit, latest: RawEventData) =>
  entry.held
    ? transformEventData(mergeHeldCopy(entry.held, latest, entry.edited))
    : latest

// A response without nested children keeps the confirmed nested children
const confirmResponse = (
  confirmed: RawEventData,
  response: RawEventData,
  id: string
) => {
  if (response.id === id) {
    return response.children?.length
      ? response
      : { ...response, children: confirmed.children }
  }

  if (!confirmed.children?.some((child) => child.id === response.id))
    return confirmed

  return {
    ...confirmed,
    children: confirmed.children.map((child) =>
      child.id === response.id ? response : child
    )
  }
}

const confirmResponses = (
  confirmed: RawEventData,
  responses: RawEventData[],
  id: string
) =>
  responses.reduce<RawEventData>(
    (copy, response) => confirmResponse(copy, response, id),
    confirmed
  )

const writeEvent = (state: EventsSliceState, event: RawEventData) => {
  eventsAdapter.setOne(state, transformEventData(event))
}

// Writes the held copies merged under the edited-field rule, else `confirmed`
const applyHeld = (
  state: EventsSliceState,
  id: string,
  entry: PendingEventEdit
) => {
  writeEvent(
    state,
    entry.held
      ? mergeHeldCopy(entry.held, entry.confirmed, entry.edited)
      : entry.confirmed
  )

  Object.values(entry.heldChildren).forEach((child) => {
    eventsAdapter.upsertOne(
      state,
      transformEventData(
        withConfirmedPlayer(
          child,
          entry.confirmed.children,
          entry.edited.childPlayers
        )
      )
    )
  })

  delete state.pendingEdits[id]
}

const pendingKeyFor = (
  pendingEdits: EventsState['pendingEdits'],
  event: RawEventData
) => {
  if (pendingEdits[event.id]) return event.id
  if (event.dataType === 'PhysicalEvent' && pendingEdits[event.parentId])
    return event.parentId

  return null
}

const holdCopy = (
  entry: PendingEventEdit,
  key: string,
  event: RawEventData
) => {
  if (event.id === key) {
    entry.held = event

    return
  }

  entry.heldChildren[event.id] = event
}

export const eventsSlice = createSlice({
  name: 'events',
  initialState: initialEventsState,
  reducers: {
    setPendingDrill: (
      state,
      action: PayloadAction<RawDrillEventData | null>
    ) => {
      state.pendingDrill = action.payload
    },
    // A copy of an event with a pending edit is held instead of written
    upsertOne: (state, action: PayloadAction<RawEventData>) => {
      const transformed = transformEventData(action.payload)
      const key = pendingKeyFor(state.pendingEdits, transformed)
      if (key) {
        holdCopy(state.pendingEdits[key], key, transformed)

        return
      }

      return eventsAdapter.upsertOne(state, { ...action, payload: transformed })
    },
    upsertMany: (state, action: PayloadAction<RawEventData[]>) => {
      const transformed = batchTransformEventData(action.payload)
      const writable: RawEventData[] = []

      transformed.forEach((event) => {
        const key = pendingKeyFor(state.pendingEdits, event)
        if (key) holdCopy(state.pendingEdits[key], key, event)
        else writable.push(event)
      })

      return eventsAdapter.upsertMany(state, {
        ...action,
        payload: writable
      })
    },
    removeOne: (state, action: PayloadAction<string>) => {
      delete state.pendingEdits[action.payload]
      eventsAdapter.removeOne(state, action.payload)
    },
    removeAll: (state) => {
      state.pendingEdits = {}
      eventsAdapter.removeAll(state)
    },
    queueEventEdit: (
      state,
      action: PayloadAction<{
        id: string
        optimistic: RawEventData
        delta: EventEditDelta
      }>
    ) => {
      const { id, optimistic, delta } = action.payload
      const existing = state.entities[id]
      const entry = state.pendingEdits[id]
      if (!existing || entry?.deleting) return

      if (!entry) {
        state.pendingEdits[id] = {
          status: 'inFlight',
          deleting: false,
          confirmed: current(existing),
          edited: editedFieldsOf(delta),
          inFlight: delta,
          queued: null,
          held: null,
          heldChildren: {}
        }
      } else if (entry.status === 'inFlight') {
        entry.queued = mergeDelta(entry.queued, delta)
        entry.edited = mergeEditedFields(entry.edited, editedFieldsOf(delta))
      } else {
        entry.status = 'inFlight'
        entry.inFlight = delta
        entry.edited = mergeEditedFields(entry.edited, editedFieldsOf(delta))
      }

      writeEvent(state, optimistic)
    },
    settleEventEdit: (
      state,
      action: PayloadAction<{ id: string; responses: RawEventData[] }>
    ) => {
      const { id, responses } = action.payload
      const entry = state.pendingEdits[id]
      if (!entry) return

      const confirmed = confirmResponses(entry.confirmed, responses, id)
      entry.confirmed = confirmed

      if (entry.queued) {
        entry.inFlight = entry.queued
        entry.queued = null

        return
      }

      entry.status = 'settling'
      entry.inFlight = null
      writeEvent(state, confirmed)
    },
    // Saves that succeeded before a step failed advance the confirmed copy
    confirmEventEditResponses: (
      state,
      action: PayloadAction<{ id: string; responses: RawEventData[] }>
    ) => {
      const { id, responses } = action.payload
      const entry = state.pendingEdits[id]
      if (!entry) return

      entry.confirmed = confirmResponses(entry.confirmed, responses, id)
    },
    revertEventEdit: (state, action: PayloadAction<string>) => {
      const entry = state.pendingEdits[action.payload]
      if (!entry) return

      if (entry.deleting) {
        entry.status = 'failed'
        entry.inFlight = null
        entry.queued = null

        return
      }

      applyHeld(state, action.payload, entry)
    },
    closeEventEditWindow: (state, action: PayloadAction<string>) => {
      const entry = state.pendingEdits[action.payload]
      if (!entry || entry.deleting || entry.status !== 'settling') return

      applyHeld(state, action.payload, entry)
    },
    markEventDeleting: (state, action: PayloadAction<string>) => {
      const entry = state.pendingEdits[action.payload]
      if (!entry) return

      entry.deleting = true
    },
    unmarkEventDeleting: (state, action: PayloadAction<string>) => {
      const entry = state.pendingEdits[action.payload]
      if (!entry) return

      entry.deleting = false
      if (entry.status !== 'inFlight') applyHeld(state, action.payload, entry)
    },
    clearEventEdit: (state, action: PayloadAction<string>) => {
      delete state.pendingEdits[action.payload]
    },
    selectEvent(state, action: PayloadAction<string>) {
      state.selectedEventId = action.payload
    },
    setSelectedColumns(state, action: PayloadAction<MetricTypeKeys[]>) {
      state.columns = action.payload
    },
    setEventValidationTableTimeDisplay(
      state,
      action: PayloadAction<EventsState['eventValidationTableTimeDisplay']>
    ) {
      state.eventValidationTableTimeDisplay = action.payload
    },
    setFlightGraphs(
      state,
      action: PayloadAction<{
        id: string
        data: {
          events: RawEventData[]
          imuData: LineChartCoordinates[]
        }
      }>
    ) {
      state.flightGraphs = {
        ...state.flightGraphs,
        [action.payload.id]: {
          data: action.payload.data
        }
      }
    }
  }
})

export const reducer = eventsSlice.reducer

export const {
  selectEvent,
  setFlightGraphs,
  setSelectedColumns,
  setEventValidationTableTimeDisplay,
  upsertOne: upsertOneEvent,
  upsertMany: upsertManyEvents,
  removeOne: removeOneEvent,
  removeAll: removeAllEvents,
  setPendingDrill,
  queueEventEdit,
  settleEventEdit,
  confirmEventEditResponses,
  revertEventEdit,
  closeEventEditWindow,
  markEventDeleting,
  unmarkEventDeleting,
  clearEventEdit
} = eventsSlice.actions
